<template>
    <div class="edit-post" v-if="currentUser">
        <div class="page-header">
            <div class="page-header-left">
                <router-link to="/admin-profile/blog" class="back-link">&#8592; Powrót do bloga</router-link>
                <h1>Edytuj wpis blogowy</h1>
            </div>
        </div>

        <div v-if="isLoading" class="loading-state">
            <p>Ładowanie wpisu...</p>
        </div>

        <div v-else-if="form.title !== undefined" class="form-layout">
            <div class="form-main section-card">
                <h2>Treść wpisu</h2>

                <div class="form-group">
                    <label for="editEntryTitle">Tytuł</label>
                    <input
                        type="text"
                        id="editEntryTitle"
                        v-model="form.title"
                        placeholder="Wpisz tytuł wpisu..."
                        required
                    >
                </div>

                <div class="form-group">
                    <label for="editEntryShortDescription">Krótki opis</label>
                    <textarea
                        id="editEntryShortDescription"
                        v-model="form.shortDescription"
                        placeholder="Wpisz krótki opis wpisu (widoczny na liście)..."
                        rows="3"
                        required
                    ></textarea>
                </div>

                <div class="form-group">
                    <label>Treść</label>
                    <CKEditor v-model="form.content"></CKEditor>
                </div>
            </div>

            <div class="form-sidebar">
                <div class="section-card">
                    <h2>Szczegóły wpisu</h2>

                    <div class="form-group">
                        <label for="editEntryAuthor">Autor</label>
                        <input
                            type="text"
                            id="editEntryAuthor"
                            v-model="form.author"
                            placeholder="Imię i nazwisko autora..."
                            required
                        >
                    </div>

                    <div class="form-group">
                        <label for="editEntryTarget">Odbiorcy</label>
                        <select id="editEntryTarget" v-model="form.target">
                            <option>do wszystkich</option>
                            <option>do specjalistów</option>
                            <option>do właścicieli zwierząt</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label for="editEntryCategory">Kategoria</label>
                        <select id="editEntryCategory" v-model="form.categoryId" required>
                            <option value="" disabled>Wybierz kategorię...</option>
                            <option :value="cat.id" v-for="cat in categoriesList" :key="cat.id">{{ cat.title }}</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label>Aktualne zdjęcie</label>
                        <div v-if="currentPhotoUrl" class="photo-preview">
                            <img :src="photoPreview || currentPhotoUrl" alt="Aktualne zdjęcie">
                        </div>
                        <label for="editEntryPhoto" class="file-label">Zmień zdjęcie (opcjonalnie)</label>
                        <input
                            type="file"
                            accept="image/*"
                            id="editEntryPhoto"
                            ref="editEntryPhoto"
                            @change="selectPhoto"
                        >
                    </div>

                    <div class="form-actions">
                        <button @click="submitForm" class="btn-primary" :disabled="isSubmitting">
                            {{ isSubmitting ? 'Zapisywanie...' : 'Zapisz zmiany' }}
                        </button>
                        <router-link to="/admin-profile/blog" class="btn-outline">Anuluj</router-link>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="error-state">
            <p>Nie udało się załadować wpisu.</p>
            <router-link to="/admin-profile/blog" class="btn-outline">Powrót do bloga</router-link>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import authHeader from '@/services/auth-header';
import CKEditor from '@/components/CKEditor.vue';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

export default {
    name: 'EditBlogPost',
    components: { CKEditor },
    setup() {
        return {
            authStore: useAuthStore(),
            appStore: useAppStore(),
        };
    },
    data() {
        return {
            postId: null,
            form: {
                title: undefined,
                shortDescription: '',
                content: '',
                author: '',
                target: 'do wszystkich',
                categoryId: '',
            },
            photo: null,
            photoPreview: null,
            currentPhotoUrl: null,
            categoriesList: [],
            isLoading: true,
            isSubmitting: false,
        };
    },
    computed: {
        currentUser() {
            return this.authStore.user;
        },
    },
    mounted() {
        if (!this.currentUser) {
            this.$router.push('/login');
        } else if (this.currentUser.role_id !== 1) {
            this.$router.push('/login');
        }
        this.postId = this.$route.params.id;
        this.getCategories();
        this.loadPost();
    },
    methods: {
        getCategories() {
            axios.post('/BlogCategorySearch', { page: 0, limit: 50 })
                .then(res => {
                    this.categoriesList = res.data.items;
                });
        },
        loadPost() {
            this.isLoading = true;
            axios.post('/BlogPostSearch', { page: 1, limit: 1000 }, authHeader())
                .then(res => {
                    const post = res.data.items.find(p => String(p.id) === String(this.postId));
                    if (post) {
                        this.form.title = post.title;
                        this.form.shortDescription = post.short_description;
                        this.form.content = post.description;
                        this.form.author = post.author;
                        this.form.target = post.target;
                        this.form.categoryId = post.category ? post.category.id : '';
                        this.currentPhotoUrl = post.photo_url;
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        selectPhoto() {
            const file = this.$refs.editEntryPhoto.files.item(0);
            if (file) {
                this.photo = file;
                this.photoPreview = URL.createObjectURL(file);
            }
        },
        submitForm() {
            if (!this.form.title || !this.form.shortDescription || !this.form.content
                || !this.form.author || !this.form.categoryId) {
                this.appStore.setIsAlert('Uzupełnij wszystkie wymagane pola');
                return;
            }

            this.isSubmitting = true;
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            };
            const ob = {
                id: Number(this.postId),
                title: this.form.title,
                short_description: this.form.shortDescription,
                description: this.form.content,
                author: this.form.author,
                target: this.form.target,
                blog_category_id: Number(this.form.categoryId),
            };
            if (this.photo) {
                ob.photo = this.photo;
            }

            axios.post('/BlogPost', ob, config)
                .then(res => {
                    if (String(res.status).charAt(0) === '2') {
                        this.appStore.setIsSuccess('Pomyślnie zapisano zmiany');
                        this.$router.push('/admin-profile/blog');
                    }
                })
                .catch(() => {
                    this.appStore.setIsAlert('Wystąpił błąd podczas zapisywania zmian');
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },
    },
};
</script>

<style scoped>
.edit-post {
    max-width: 1200px;
    margin: 0 auto;
    padding: 32px 16px;
    font-family: sans-serif;
    box-sizing: border-box;
}

.page-header {
    margin-bottom: 32px;
}

.back-link {
    display: inline-block;
    font-size: 1.4rem;
    color: #10b981;
    text-decoration: none;
    font-weight: 600;
    margin-bottom: 8px;
    transition: color 0.2s;
}

.back-link:hover {
    color: #059669;
}

.page-header h1 {
    font-size: 2.8rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
}

.form-layout {
    display: flex;
    gap: 24px;
    align-items: flex-start;
}

.form-main {
    flex: 1 1 0;
    min-width: 0;
}

.form-sidebar {
    width: 320px;
    flex-shrink: 0;
}

@media (max-width: 900px) {
    .form-layout {
        flex-direction: column;
    }
    .form-sidebar {
        width: 100%;
    }
}

.section-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.08);
    padding: 24px;
    box-sizing: border-box;
    width: 100%;
}

.section-card h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 20px 0;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 20px;
}

.form-group label,
.file-label {
    font-size: 1.5rem;
    font-weight: 600;
    color: #374151;
}

.file-label {
    margin-top: 8px;
}

.form-group input[type="text"],
.form-group input[type="file"],
.form-group textarea,
.form-group select {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 1.5rem;
    outline: none;
    transition: border-color 0.2s;
    resize: vertical;
    box-sizing: border-box;
    width: 100%;
}

.form-group input[type="file"] {
    padding: 8px;
    cursor: pointer;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
}

.photo-preview {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    margin-bottom: 8px;
}

.photo-preview img {
    width: 100%;
    max-height: 200px;
    object-fit: cover;
    display: block;
}

.form-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 8px;
}

.btn-primary {
    background-color: #10b981;
    color: #fff;
    padding: 12px 20px;
    border-radius: 8px;
    border: none;
    font-size: 1.5rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    display: block;
    text-align: center;
    transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
    background-color: #059669;
}

.btn-primary:disabled {
    background-color: #6ee7b7;
    cursor: not-allowed;
}

.btn-outline {
    background-color: transparent;
    color: #6b7280;
    padding: 12px 20px;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    font-size: 1.5rem;
    cursor: pointer;
    text-decoration: none;
    display: block;
    text-align: center;
    transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
    border-color: #9ca3af;
    color: #374151;
}

.loading-state,
.error-state {
    text-align: center;
    padding: 64px 32px;
    color: #9ca3af;
    font-size: 1.6rem;
}

.error-state .btn-outline {
    display: inline-block;
    margin-top: 16px;
}

@media (max-width: 480px) {
    .edit-post {
        padding: 16px 12px;
    }
    .page-header {
        margin-bottom: 20px;
    }
    .page-header h1 {
        font-size: 2.2rem;
    }
    .section-card {
        padding: 16px 12px;
        border-radius: 8px;
    }
    .section-card h2 {
        font-size: 1.8rem;
    }
    .form-layout {
        gap: 16px;
    }
}
</style>
